import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';
import GroupLeaderBottomNav from '../../../components/GroupLeaderBottomNav';
import { Html5Qrcode } from 'html5-qrcode';
import confetti from 'canvas-confetti';
import {
  Bell,
  UserCircle,
  Camera,
  User,
  Check,
  CheckCircle,
  XCircle,
  Warning,
  Info,
  WifiSlash,
  CloudArrowUp,
  ArrowsClockwise
} from '@phosphor-icons/react';

export default function GroupLeaderScanner() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [activeEvent, setActiveEvent] = useState(null);
  const [userId, setUserId] = useState(null);
  const [selfCheckedIn, setSelfCheckedIn] = useState(false);
  const [scannedMembers, setScannedMembers] = useState([]);
  const [lastScan, setLastScan] = useState(null);
  const [scanResult, setScanResult] = useState(null);
  const qrScannerRef = useRef(null);
  
  // Offline sync state
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [queueCount, setQueueCount] = useState(0);
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);

  // IndexedDB utilities
  const initDB = () => {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open('AttendanceDB', 1);
      
      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve(request.result);
      
      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        if (!db.objectStoreNames.contains('offlineQueue')) {
          const objectStore = db.createObjectStore('offlineQueue', { keyPath: 'id', autoIncrement: true });
          objectStore.createIndex('timestamp', 'timestamp', { unique: false });
          objectStore.createIndex('synced', 'synced', { unique: false });
        }
      };
    });
  };

  const addToOfflineQueue = async (attendanceRecord) => {
    try {
      const db = await initDB();
      const transaction = db.transaction(['offlineQueue'], 'readwrite');
      const store = transaction.objectStore('offlineQueue');
      
      const queueItem = {
        ...attendanceRecord,
        timestamp: new Date().toISOString(),
        synced: false
      };
      
      await store.add(queueItem);
      await updateQueueCount();
      console.log('✅ Added to offline queue:', queueItem);
    } catch (error) {
      console.error('❌ Failed to add to offline queue:', error);
    }
  };

  const getOfflineQueue = async () => {
    try {
      const db = await initDB();
      const transaction = db.transaction(['offlineQueue'], 'readonly');
      const store = transaction.objectStore('offlineQueue');
      
      return new Promise((resolve, reject) => {
        const request = store.getAll();
        request.onsuccess = () => resolve(request.result.filter(item => !item.synced));
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      console.error('❌ Failed to get offline queue:', error);
      return [];
    }
  };

  const markAsSynced = async (id) => {
    try {
      const db = await initDB();
      const transaction = db.transaction(['offlineQueue'], 'readwrite');
      const store = transaction.objectStore('offlineQueue');
      
      const item = await new Promise((resolve, reject) => {
        const request = store.get(id);
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
      });
      
      if (item) {
        item.synced = true;
        await store.put(item);
      }
    } catch (error) {
      console.error('❌ Failed to mark as synced:', error);
    }
  };

  const updateQueueCount = async () => {
    const queue = await getOfflineQueue();
    setQueueCount(queue.length);
  };

  const syncQueue = async () => {
    if (isSyncing) return;
    
    setIsSyncing(true);
    setSyncStatus('Syncing...');
    
    try {
      const queue = await getOfflineQueue();
      console.log(`📤 Syncing ${queue.length} items...`);
      
      for (const item of queue) {
        try {
          // Insert attendance record using upsert to prevent duplicates
          const { error } = await supabase
            .from('attendance_records')
            .upsert({
              event_id: item.event_id,
              user_id: item.user_id,
              marked_by: item.marked_by,
              marked_at: item.marked_at,
              is_self_marked: item.is_self_marked
            }, {
              onConflict: 'event_id,user_id',
              ignoreDuplicates: true
            });
          
          if (error) {
            console.error('❌ Sync error for item:', item.id, error);
            continue;
          }
          
          // Send push notification if not self-marked
          if (!item.is_self_marked) {
            try {
              await supabase.functions.invoke('send-push-notification', {
                body: {
                  userIds: [item.user_id],
                  title: '✓ Attendance Marked',
                  body: `Your attendance has been marked for ${item.event_title}`,
                  url: '/member/home',
                  data: {
                    type: 'attendance_marked',
                    event_id: item.event_id,
                    event_title: item.event_title
                  }
                }
              });
            } catch (notifError) {
              console.error('❌ Notification error:', notifError);
            }
          }
          
          await markAsSynced(item.id);
          console.log('✅ Synced item:', item.id);
        } catch (error) {
          console.error('❌ Failed to sync item:', item.id, error);
        }
      }
      
      await updateQueueCount();
      setSyncStatus('Sync complete!');
      setTimeout(() => setSyncStatus(null), 3000);
      
      // Refresh scanned members list
      if (activeEvent) {
        await loadScannedMembers();
      }
    } catch (error) {
      console.error('❌ Sync failed:', error);
      setSyncStatus('Sync failed');
      setTimeout(() => setSyncStatus(null), 3000);
    } finally {
      setIsSyncing(false);
    }
  };

  // Network status monitoring
  useEffect(() => {
    const handleOnline = () => {
      console.log('🟢 Network online');
      setIsOnline(true);
      setTimeout(() => syncQueue(), 1000);
    };

    const handleOffline = () => {
      console.log('🔴 Network offline');
      setIsOnline(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    updateQueueCount();

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    checkActiveEvent();
    
    return () => {
      if (qrScannerRef.current) {
        qrScannerRef.current.stop().catch(err => console.error('Error stopping scanner:', err));
      }
    };
  }, []);

  useEffect(() => {
    if (scanning && activeEvent) {
      initializeScanner();
    }
    
    return () => {
      if (qrScannerRef.current) {
        qrScannerRef.current.stop().catch(err => console.error('Error stopping scanner:', err));
        qrScannerRef.current = null;
      }
    };
  }, [scanning, activeEvent]);

  const initializeScanner = async () => {
    if (qrScannerRef.current) return;

    try {
      const html5QrCode = new Html5Qrcode('qr-reader');
      qrScannerRef.current = html5QrCode;

      const config = { 
        fps: 10,
        qrbox: { width: 250, height: 250 }
      };

      await html5QrCode.start(
        { facingMode: "environment" },
        config,
        onScanSuccess,
        onScanError
      );
    } catch (error) {
      console.error('❌ Error starting camera:', error);
      setScanResult({
        type: 'error',
        message: 'Camera access denied',
        details: 'Please allow camera permissions to scan QR codes'
      });
    }
  };

  const onScanSuccess = (decodedText) => {
    handleScan(decodedText);
  };

  const onScanError = (error) => {
    // Ignore scan errors
  };

  const checkActiveEvent = async () => {
    try {
      setLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate('/login');
        return;
      }

      setUserId(user.id);

      const { data: userData } = await supabase
        .from('users')
        .select('org_id')
        .eq('id', user.id)
        .single();

      // Check for active event
      const now = new Date();
      const today = now.toISOString().split('T')[0];
      const currentTime = now.toTimeString().slice(0, 5);

      const { data: event } = await supabase
        .from('events')
        .select('*')
        .eq('org_id', userData.org_id)
        .eq('start_date', today)
        .lte('start_time', currentTime)
        .gte('end_time', currentTime)
        .maybeSingle();

      setActiveEvent(event);

      if (event) {
        // Check if user already self-checked in
        const { data: existing } = await supabase
          .from('attendance_records')
          .select('id')
          .eq('event_id', event.id)
          .eq('user_id', user.id)
          .maybeSingle();

        setSelfCheckedIn(!!existing);

        // Load scanned members for this event
        await loadScannedMembers(event.id, user.id);
      }

    } catch (error) {
      console.error('Error checking active event:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadScannedMembers = async (eventId = null, currentUserId = null) => {
    try {
      const eid = eventId || activeEvent?.id;
      const uid = currentUserId || userId;

      if (!eid || !uid) return;

      const { data } = await supabase
        .from('attendance_records')
        .select(`
          id,
          marked_at,
          user_id,
          users:user_id (
            id,
            name,
            email,
            profile_photo_url,
            avatar_url
          )
        `)
        .eq('event_id', eid)
        .eq('marked_by', uid)
        .eq('is_self_marked', false)
        .order('marked_at', { ascending: false })
        .limit(10);

      setScannedMembers(data || []);
    } catch (error) {
      console.error('Error loading scanned members:', error);
    }
  };

  const handleSelfCheckIn = async () => {
    if (!activeEvent || selfCheckedIn) return;

    try {
      const { data: { user } } = await supabase.auth.getUser();

      // Check if online or offline
      if (!isOnline) {
        await addToOfflineQueue({
          event_id: activeEvent.id,
          event_title: activeEvent.title,
          user_id: user.id,
          marked_by: user.id,
          marked_at: new Date().toISOString(),
          is_self_marked: true
        });

        setSelfCheckedIn(true);
        confetti({
          particleCount: 100,
          spread: 70,
          origin: { y: 0.6 }
        });
        return;
      }

      // Online - mark directly
      const { error } = await supabase
        .from('attendance_records')
        .insert({
          event_id: activeEvent.id,
          user_id: user.id,
          marked_by: user.id,
          marked_at: new Date().toISOString(),
          is_self_marked: true
        });

      if (error) throw error;

      setSelfCheckedIn(true);
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 }
      });

    } catch (error) {
      console.error('Error self check-in:', error);
      alert('Failed to mark attendance. Please try again.');
    }
  };

  const handleScan = async (scannedData) => {
    if (!scannedData || !activeEvent) return;

    // Prevent duplicate scans
    if (scannedData === lastScan) return;
    setLastScan(scannedData);

    try {
      const qrSignature = scannedData.trim();

      // Look up user by qr_signature
      const { data: scannedUser } = await supabase
        .from('users')
        .select('id, name, email, profile_photo_url, avatar_url')
        .eq('qr_signature', qrSignature)
        .single();

      if (!scannedUser) {
        setScanResult({
          type: 'error',
          message: 'Invalid QR Code',
          details: 'QR code not found in system'
        });
        setTimeout(() => {
          setScanResult(null);
          setLastScan(null);
        }, 3000);
        return;
      }

      // Check if already marked
      const { data: existing } = await supabase
        .from('attendance_records')
        .select('id')
        .eq('event_id', activeEvent.id)
        .eq('user_id', scannedUser.id)
        .maybeSingle();

      if (existing) {
        setScanResult({
          type: 'warning',
          message: 'Already Checked In',
          details: `${scannedUser.name} has already been marked present`
        });
        setTimeout(() => {
          setScanResult(null);
          setLastScan(null);
        }, 3000);
        return;
      }

      const { data: { user: currentUser } } = await supabase.auth.getUser();

      // Check if online or offline
      if (!isOnline) {
        await addToOfflineQueue({
          event_id: activeEvent.id,
          event_title: activeEvent.title,
          user_id: scannedUser.id,
          user_name: scannedUser.name,
          marked_by: currentUser.id,
          marked_at: new Date().toISOString(),
          is_self_marked: false
        });

        setScanResult({
          type: 'success',
          message: 'Saved Offline',
          details: `${scannedUser.name} - Will sync when online`,
          user: scannedUser,
          offline: true
        });

        setTimeout(() => {
          setScanResult(null);
          setLastScan(null);
        }, 2000);
        
        return;
      }

      // Online - mark attendance
      const { error: attendanceError } = await supabase
        .from('attendance_records')
        .insert({
          event_id: activeEvent.id,
          user_id: scannedUser.id,
          marked_by: currentUser.id,
          marked_at: new Date().toISOString(),
          is_self_marked: false
        });

      if (attendanceError) throw attendanceError;

      // Send push notification
      try {
        await supabase.functions.invoke('send-push-notification', {
          body: {
            userIds: [scannedUser.id],
            title: '✓ Attendance Marked',
            body: `Your attendance has been marked for ${activeEvent.title}`,
            url: '/member/home',
            data: {
              type: 'attendance_marked',
              event_id: activeEvent.id,
              event_title: activeEvent.title
            }
          }
        });
      } catch (notifError) {
        console.error('❌ Notification error:', notifError);
      }

      // Trigger confetti
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 }
      });

      setScanResult({
        type: 'success',
        message: 'Attendance Marked!',
        details: scannedUser.name,
        user: scannedUser
      });

      // Refresh scanned members list
      await loadScannedMembers();

      setTimeout(() => {
        setScanResult(null);
        setLastScan(null);
      }, 2000);

    } catch (error) {
      console.error('Error marking attendance:', error);
      setScanResult({
        type: 'error',
        message: 'Failed to mark attendance',
        details: error.message
      });
      setTimeout(() => {
        setScanResult(null);
        setLastScan(null);
      }, 3000);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading scanner...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <style>{`
        .liquid-glass-button {
          border-radius: 200px;
          height: 40px;
          width: 40px;
          backdrop-filter: blur(10px); 
          box-shadow: 0 -2px 4px 0 rgba(0, 0, 0, 0.20) inset, 0 1px 2px 0 rgba(255, 255, 255, 0.40) inset;
          background: #2C5DE5;
          border: 1px solid rgba(255, 255, 255, 0.05);
        }
        .liquid-glass-button-green {
          border-radius: 200px;
          height: 40px;
          width: 40px;
          backdrop-filter: blur(10px); 
          box-shadow: 0 -2px 4px 0 rgba(0, 0, 0, 0.20) inset, 0 1px 2px 0 rgba(255, 255, 255, 0.40) inset;
          background: #2B9646;
          border: 1px solid rgba(255, 255, 255, 0.05);
        }
        .liquid-glass-button-grey {
          border-radius: 200px;
          height: 40px;
          width: 40px;
          backdrop-filter: blur(10px); 
          box-shadow: 0 -2px 4px 0 rgba(0, 0, 0, 0.20) inset, 0 1px 2px 0 rgba(255, 255, 255, 0.40) inset;
          background: #e2e8f0;
          border: 1px solid rgba(255, 255, 255, 0.05);
        }
      `}</style>

      <div className="min-h-screen bg-white pb-20">
        {/* Header */}
        <div className="px-6 pt-10 pb-6 flex items-start justify-between">
          <h1 className="text-2xl font-semibold text-slate-800">Scan</h1>
          <div className="bg-slate-50 border border-slate-200 rounded-[20px] p-2">
            <Bell size={20} weight="fill" className="text-[#64748B]" />
          </div>
        </div>

        {/* Offline/Sync Status */}
        {!isOnline && (
          <div className="px-6 mt-4">
            <div className="bg-orange-50 border-l-4 border-orange-500 p-4 rounded-lg">
              <div className="flex items-center gap-3">
                <WifiSlash size={24} weight="fill" className="text-orange-600" />
                <div>
                  <p className="text-sm font-medium text-orange-800">Offline Mode</p>
                  <p className="text-xs text-orange-700">Scans will be saved and synced when connection returns</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {queueCount > 0 && (
          <div className="px-6 mt-4">
            <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <CloudArrowUp size={24} weight="fill" className="text-blue-600" />
                  <div>
                    <p className="text-sm font-medium text-blue-800">
                      {queueCount} {queueCount === 1 ? 'scan' : 'scans'} pending sync
                    </p>
                    <p className="text-xs text-blue-700">
                      {isOnline ? 'Ready to sync' : 'Will sync when online'}
                    </p>
                  </div>
                </div>
                {isOnline && (
                  <button
                    onClick={syncQueue}
                    disabled={isSyncing}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                  >
                    <ArrowsClockwise size={16} weight="bold" className={isSyncing ? 'animate-spin' : ''} />
                    {isSyncing ? 'Syncing...' : 'Sync'}
                  </button>
                )}
              </div>
              {syncStatus && (
                <p className="text-xs text-blue-700 mt-2 font-medium">{syncStatus}</p>
              )}
            </div>
          </div>
        )}

        {/* Scanner Card */}
        <div className="px-6 mt-4">
          <div className="bg-white rounded-[20px] border border-slate-200 overflow-hidden">
            {activeEvent && scanning ? (
              <>
                <div id="qr-reader"></div>
                <div className="p-4">
                  <button
                    onClick={async () => {
                      if (qrScannerRef.current) {
                        try {
                          await qrScannerRef.current.stop();
                          qrScannerRef.current = null;
                        } catch (err) {
                          console.error('Error stopping camera:', err);
                        }
                      }
                      setScanning(false);
                    }}
                    className="w-full bg-red-600 text-white py-3 rounded-xl font-semibold hover:bg-red-700 transition-colors"
                  >
                    Stop Scanning
                  </button>
                </div>
              </>
            ) : (
              <div className="flex flex-col items-center justify-center py-16 px-6">
                <Camera size={100} className="text-[#64748b] mb-4" weight="duotone" />
                {!activeEvent ? (
                  <>
                    <p className="text-[#1E293B] font-medium text-xl mb-1">No active event</p>
                    <p className="text-[#475569] text-sm text-center mb-6">
                      Scanning only available during <br/> an active event
                    </p>
                  </>
                ) : (
                  <p className="text-[#1E293B] font-medium text-xl mb-6">Ready to scan</p>
                )}
                <button
                  onClick={() => {
                    if (activeEvent) {
                      setScanning(true);
                    }
                  }}
                  disabled={!activeEvent}
                  className={`px-8 py-3 rounded-xl font-semibold transition-colors ${
                    activeEvent
                      ? 'bg-blue-600 text-white hover:bg-blue-700'
                      : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                  }`}
                >
                  Scan code
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Scan Result */}
        {scanResult && (
          <div className="px-6 mt-4">
            <div className={`rounded-[20px] p-6 ${
              scanResult.type === 'success' ? (scanResult.offline ? 'bg-orange-50 border-2 border-orange-200' : 'bg-green-50 border-2 border-green-200') :
              scanResult.type === 'warning' ? 'bg-yellow-50 border-2 border-yellow-200' :
              'bg-red-50 border-2 border-red-200'
            }`}>
              <div className="flex items-start gap-3">
                {scanResult.type === 'success' && (
                  scanResult.offline ? (
                    <CloudArrowUp size={32} weight="fill" className="text-orange-600 flex-shrink-0" />
                  ) : (
                    <CheckCircle size={32} weight="fill" className="text-green-600 flex-shrink-0" />
                  )
                )}
                {scanResult.type === 'warning' && (
                  <Warning size={32} weight="fill" className="text-yellow-600 flex-shrink-0" />
                )}
                {scanResult.type === 'error' && (
                  <XCircle size={32} weight="fill" className="text-red-600 flex-shrink-0" />
                )}
                <div className="flex-1">
                  <h3 className={`font-bold text-lg mb-1 ${
                    scanResult.type === 'success' ? (scanResult.offline ? 'text-orange-900' : 'text-green-900') :
                    scanResult.type === 'warning' ? 'text-yellow-900' :
                    'text-red-900'
                  }`}>
                    {scanResult.message}
                  </h3>
                  <p className={`text-sm ${
                    scanResult.type === 'success' ? (scanResult.offline ? 'text-orange-700' : 'text-green-700') :
                    scanResult.type === 'warning' ? 'text-yellow-700' :
                    'text-red-700'
                  }`}>
                    {scanResult.details}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Self Check-in Card */}
        <div className="px-6 mt-4">
          <div 
            onClick={handleSelfCheckIn}
            className={`rounded-[20px] p-4 flex items-center justify-between transition-all ${
              activeEvent && !selfCheckedIn
                ? 'cursor-pointer hover:shadow-lg'
                : ''
            } ${
              activeEvent && !selfCheckedIn
                ? 'bg-white border border-slate-200'
                : selfCheckedIn
                ? 'bg-white border border-slate-200'
                : 'bg-white border border-slate-200'
            }`}
          >
            <div>
              <p className={`font-medium text-lg mb-1 ${
                activeEvent && !selfCheckedIn ? 'text-[#1e293b]' : selfCheckedIn ? 'text-[#1e293b]' : 'text-[#1e293b]'
              }`}>
                Self check-in
              </p>
              <p className={`text-xs font-regular ${
                activeEvent && !selfCheckedIn ? 'text-[#475569]' : selfCheckedIn ? 'text-[#475569]' : 'text-[#475569]'
              }`}>
                {activeEvent && !selfCheckedIn
                  ? 'Click to mark attendance!'
                  : selfCheckedIn
                  ? 'Attendance marked!'
                  : 'Available during an active event'}
              </p>
            </div>
            <div className={`flex items-center justify-center ${
              activeEvent && !selfCheckedIn
                ? 'liquid-glass-button'
                : selfCheckedIn
                ? 'liquid-glass-button-green'
                : 'liquid-glass-button-grey'
            }`}>
              <Check 
                size={24} 
                weight="light"
                className="text-white"
              />
            </div>
          </div>
        </div>

        {/* Members Scanned - Always visible */}
        <div className="px-6 mt-6">
          <div className="bg-white rounded-[20px] border border-slate-200 p-4">
            <h3 className="font-semibold text-[#1e293b] text-xl mb-1">Members Scanned</h3>
            <p className="text-base text-[#475569] mb-3">
              {activeEvent ? activeEvent.title : 'No active event'}
            </p>
            
            {scannedMembers.length > 0 ? (
              <div className="space-y-2">
                {scannedMembers.map((record) => (
                  <div key={record.id} className="flex items-center justify-between py-2">
                    <div className="flex items-center gap-3">
                      <CheckCircle size={16} weight="fill" className="text-[#2b9646]" />
                      <div>
                        <p className="text-sm font-medium text-[#475569]">
                          {record.users?.name || 'Unknown'}
                        </p>
                      </div>
                    </div>
                    <p className="text-sm text-[#475569]">
                      {new Date(record.marked_at).toLocaleTimeString('en-US', {
                        hour: 'numeric',
                        minute: '2-digit',
                        hour12: true
                      })}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="py-8 flex flex-col items-center justify-center">
                <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mb-3">
                  <User size={24} weight="light" className="text-slate-400" />
                </div>
                <p className="text-sm text-[#475569]">No members scanned yet</p>
              </div>
            )}
          </div>
        </div>
      </div>

      <GroupLeaderBottomNav />
    </>
  );
}